import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useOrdensServico } from '@/hooks/useOrdensServico';
import { PillTabs } from '@/components/common/PillTabs';
import { TabelaOS } from './TabelaOS';
import {
  agruparOrdensPorStatus,
  filtrarOrdens,
  type EstadoOS,
} from '@/lib/os-agrupamento';

type TabAdmin = Exclude<EstadoOS, 'recusada' | 'pendente'>;

/**
 * Bolinha (ou ✓/✕) colorida por estado à frente do label da sub-aba. Usa as
 * mesmas classes DS (`os-led--*`) que o indicador no avatar dos clientes do
 * Painel, para a paleta de status ser **uma só** em toda a app.
 */
function IndicadorEstado({ estado }: { estado: TabAdmin }) {
  switch (estado) {
    case 'disponiveis':
      return (
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full os-led--disp os-led--pulse"
        />
      );
    case 'aceita':
      return (
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full os-led--aceita os-led--pulse"
        />
      );
    case 'em_execucao':
      return (
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full os-led--exec os-led--pulse"
        />
      );
    case 'concluida':
      return (
        <span
          aria-hidden
          className="inline-flex items-center justify-center h-3 w-3 rounded-full text-[9px] os-led--glifo"
        >
          ✓
        </span>
      );
    case 'cancelada':
      return (
        <span
          aria-hidden
          className="inline-flex items-center justify-center h-3 w-3 rounded-full text-[9px] os-led--glifo"
        >
          ✕
        </span>
      );
  }
}

interface ListaOSAdminProps {
  termoBusca?: string;
  prestadorId?: string;
  periodo?: 'hoje' | 'semana' | 'mes' | 'tudo';
}

/** Lista de OS (admin/atendente) com sub-abas por estado. Substitui o Kanban. */
export function ListaOSAdmin({ termoBusca = '', prestadorId, periodo = 'tudo' }: ListaOSAdminProps) {
  const { t } = useTranslation();
  const { data: todasOrdens, isLoading } = useOrdensServico();
  const [tab, setTab] = useState<TabAdmin>('disponiveis');

  const ordensFiltradas = useMemo(
    () => filtrarOrdens(todasOrdens ?? [], { termoBusca, prestadorId, periodo }),
    [todasOrdens, termoBusca, prestadorId, periodo],
  );
  const grupos = useMemo(() => agruparOrdensPorStatus(ordensFiltradas), [ordensFiltradas]);

  const tabs: { value: TabAdmin; texto: string }[] = [
    { value: 'disponiveis', texto: t('orders.status.available') },
    { value: 'aceita', texto: t('orders.status.accepted') },
    { value: 'em_execucao', texto: t('orders.status.inProgress') },
    { value: 'concluida', texto: t('orders.status.completed') },
    { value: 'cancelada', texto: t('orders.status.cancelled') },
  ];

  const lista = grupos[tab];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PillTabs<TabAdmin>
        value={tab}
        options={tabs.map((o) => ({
          value: o.value,
          label: (
            <span className="inline-flex items-center gap-1.5">
              <IndicadorEstado estado={o.value} />
              {o.texto}
            </span>
          ),
          count: grupos[o.value].length,
        }))}
        onChange={setTab}
      />
      {lista.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {t('orders.noOrders')}
        </div>
      ) : (
        <TabelaOS lista={lista} mostrarPrestador />
      )}
    </div>
  );
}
